// Thin REST wrappers for the per-user cross-device clipboard.  No
// Replicache integration — the list is loaded on demand whenever the
// popover opens and refreshed after each mutation.

export type ClipboardEntry = {
  id: string;
  kind: "text" | "image";
  textContent: string | null;
  mimeType: string;
  sizeBytes: number | null;
  uploadStatus: "pending" | "uploaded" | "failed";
  createdAt: string;
};

function authHeaders(accessToken: string): HeadersInit {
  return { Authorization: `Bearer ${accessToken}` };
}

export async function listClipboard(
  accessToken: string,
): Promise<ClipboardEntry[]> {
  const res = await fetch("/api/clipboard", {
    headers: authHeaders(accessToken),
  });
  if (!res.ok) throw new Error(`list ${res.status}`);
  return (await res.json()) as ClipboardEntry[];
}

export async function postClipboardText(
  accessToken: string,
  content: string,
  mimeType = "text/plain",
): Promise<ClipboardEntry> {
  const res = await fetch("/api/clipboard", {
    method: "POST",
    headers: {
      ...authHeaders(accessToken),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ kind: "text", content, mimeType }),
  });
  if (!res.ok) throw new Error(`post text ${res.status}`);
  return (await res.json()) as ClipboardEntry;
}

// Two-phase image flow: presign → PUT to MinIO → mark complete.
export async function postClipboardImage(
  accessToken: string,
  blob: Blob,
): Promise<{ id: string }> {
  const presign = await fetch("/api/clipboard/image", {
    method: "POST",
    headers: {
      ...authHeaders(accessToken),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      mimeType: blob.type || "application/octet-stream",
      sizeBytes: blob.size,
    }),
  });
  if (!presign.ok) {
    if (presign.status === 415) {
      throw new Error("This image type isn't supported.");
    }
    throw new Error(`presign ${presign.status}`);
  }
  const { id, uploadUrl } = (await presign.json()) as {
    id: string;
    uploadUrl: string;
    uploadMethod: "PUT";
    mimeType: string;
  };

  const put = await fetch(uploadUrl, {
    method: "PUT",
    body: blob,
    headers: { "Content-Type": blob.type || "application/octet-stream" },
  });
  if (!put.ok) throw new Error(`upload ${put.status}`);

  const complete = await fetch(`/api/clipboard/${id}/complete`, {
    method: "POST",
    headers: authHeaders(accessToken),
  });
  if (!complete.ok) throw new Error(`complete ${complete.status}`);
  return { id };
}

export async function getClipboardDownloadUrl(
  accessToken: string,
  id: string,
): Promise<string> {
  const res = await fetch(`/api/clipboard/${id}/download-url`, {
    headers: authHeaders(accessToken),
  });
  if (!res.ok) throw new Error(`download-url ${res.status}`);
  const { url } = (await res.json()) as { url: string };
  return url;
}

export async function deleteClipboardEntry(
  accessToken: string,
  id: string,
): Promise<void> {
  const res = await fetch(`/api/clipboard/${id}`, {
    method: "DELETE",
    headers: authHeaders(accessToken),
  });
  if (!res.ok) throw new Error(`delete ${res.status}`);
}
